<template>
    <basic-card>
        <template slot="header">
            {{displayName}} [Showing in {{internalTargetCurrency}}]
        </template>
        <template v-if="loading">
            <loader indefinite/>
        </template>
        <template v-else>
            <timeline-chart
                    :dataseries="series"
                    :disabled-series="disabledSeries"
                    :domain="fromToDates"
                    @indexChanged="handleIndexChanged"
                    :stacked="stackedChart"
                    :show-total="showTotal"
            />
            <div class="legend-container">
                <div class="legend-item">
                    <div class="legend-color-symbol"
                         :style="{'background-image': 'linear-gradient(45deg, #ffffff 25%, #000000 25%, #000000 50%, #ffffff 50%, #ffffff 75%, #000000 75%, #000000 100%)'}"></div>
                    <div class="legend-caption">Total (from visible)</div>
                    <div style="text-align: right">{{$formats.formatAmount(totalForActualIndex)}}</div>
                </div>
                <div v-for="oneSeries in series" class="legend-item">
                    <div class="legend-color-symbol"
                         :style="{'background-color': disabledSeries.includes(oneSeries.name) ? '#d5d5d5' : oneSeries.color}"
                         @click="toggleSeries(oneSeries.name)">
                        <icon :icon="['fal', 'times']" v-if="disabledSeries.includes(oneSeries.name)"/>
                    </div>
                    <div class="legend-caption">{{oneSeries.name}}</div>
                    <div style="text-align: right">{{ disabledSeries.includes(oneSeries.name) ? '' :
                        $formats.formatAmount(valuesForActualIndex[oneSeries.name])}}
                    </div>
                </div>
            </div>
            <form-wrapper>
                <form-select-input v-model="internalTargetCurrency" caption="Currency" :options="currencies"/>
            </form-wrapper>
        </template>
    </basic-card>
</template>

<script>
    import {mapGetters} from "vuex";
    import BasicCard from "../../layout/BasicCard";
    import TimelineChart from "../../charts/TimelineChart";
    import chroma from "chroma-js"
    import FormSelectInput from "../../form/FormSelectInput";
    import FormWrapper from "../../form/FormWrapper";
    import Loader from "../../Loader";
    import BankingStore from "../../../stores/banking-store";

    export default {
        name: "AccountBalancesForAccountWidget",
        components: {Loader, FormWrapper, FormSelectInput, TimelineChart, BasicCard},
        props: {
            displayName: String,
            accountQuery: String,
            startDateExpr: String,
            endDateExpr: String,
            targetCurrency: String,
            showOnlyTotal: Boolean,
            stackedChart: Boolean,
            showTotal: Boolean
        },
        data() {
            return {
                chart: null,
                startDate: null,
                endDate: null,
                actualIndex: null,
                valuesForActualIndex: {},
                totalForActualIndex: 0,
                disabledSeries: [],
                timelines: [],
                loading: true,
                internalTargetCurrency: "CZK"
            }
        },
        created() {
            if (this.$store._modules.root._children[this.accountQuery] == undefined) {
                this.$store.registerModule(this.accountQuery, BankingStore());
            }

            this.refreshData();
        },
        methods: {
            refreshData() {
                this.loading = true;
                let processAll = () => {
                    let fullQuery = this.$store.getters[`accountQueries/queryById`](this.accountQuery);
                    Promise.all([
                        this.convertRelativeDate(this.endDateExpr).then(date => this.endDate = date),
                        this.convertRelativeDate(this.startDateExpr).then(date => this.startDate = date),
                        this.$store.dispatch(`${this.accountQuery}/refreshAccountsPaged`, {page: 0, size: 500, query: fullQuery})
                    ]).then(() => {
                        this.$store.dispatch(`${this.accountQuery}/fetchAccountBalanceTimelines`, {
                            dateFrom: this.startDate.split("T")[0],
                            dateTo: this.endDate.split("T")[0],

                        })
                            .then(timelines => {
                                this.$set(this, 'timelines', timelines);
                                this.$set(this, 'loading', false);
                                this.$forceUpdate();
                            })
                    })
                }
                this.$store.dispatch(`accountQueries/refreshAccountQueries`)
                    .then(processAll)

            },
            toggleSeries(seriesName) {
                if (this.disabledSeries.includes(seriesName)) {
                    this.disabledSeries.splice(this.disabledSeries.indexOf(seriesName), 1);
                } else {
                    this.disabledSeries.push(seriesName)
                }
            },
            handleIndexChanged(newIndex) {
                this.actualIndex = newIndex;
                this.totalForActualIndex = 0;
                this.series.filter(oneSeries => !this.disabledSeries.includes(oneSeries.name))
                    .forEach(oneSeries => {
                        let foundValue = oneSeries.values.find(val => this.$moment(val.x).isSame(this.$moment(this.actualIndex).add(1, "days"), 'days'));
                        if (foundValue == undefined) foundValue = {y: 0}
                        this.totalForActualIndex += foundValue.y;
                        this.$set(this.valuesForActualIndex, oneSeries.name, foundValue.y);
                    })
            }
        },
        watch: {
            targetCurrency: {
                handler(newVal) {

                    this.$set(this, 'internalTargetCurrency', newVal);
                },
                immediate: true
            }
        },
        computed: {
            ...mapGetters({
                "currencies": "currencies/currenciesOptions",
                accountOwnerships: "accountOwnerships/accountOwnerships",
                getValuesForCurrency: "currencies/getValuesForCurrency"
            }),
            selectedAccounts() {
                return this.$store.getters[`${this.accountQuery}/accounts`]
            },
            fromToDates() {
                return [this.startDate, this.endDate]
            },
            reportRates() {
                let rates = this.getValuesForCurrency(this.internalTargetCurrency).filter(rate => this.$moment(rate.date).isBetween(this.startDate, this.endDate, null, '[]'));
                return rates;
            },
            reportRatesProvider() {
                return (idx) => {
                    if (this.internalTargetCurrency == 'CZK') return 1;
                    else {
                        if (idx >= this.reportRates.length - 1) {
                            return this.reportRates[this.reportRates.length - 1]?.value / 1000;
                        }
                        return this.reportRates[idx]?.value / 1000;
                    }
                }
            },
            chartId() {
                return this.displayName + this.account;
            },
            colorScale() {
                return chroma.scale('Spectral').classes(this.selectedAccounts.length);
            },
            series() {
                return this.selectedAccounts.map((accObj, idx) => {
                        let color = this.colorScale(idx / this.selectedAccounts.length).hex();
                        let accRates = this.getValuesForCurrency(accObj.currency);
                        accRates = accRates.filter(rate => this.$moment(rate.date).isBetween(this.startDate, this.endDate, null, '[]'))
                        let accRateProvider = (idx) => {
                            if (accObj.currency == 'CZK') return 1;
                            else {
                                if (idx >= accRates.length - 1) {
                                    return (accRates[accRates.length - 1]?.value ?? 1000) / 1000;
                                }
                                return accRates[idx].value / 1000;
                            }
                        }
                        if (accObj.vizColor != null && accObj.vizColor != '') {
                            color = accObj.vizColor;
                        }
                        return {
                            name: accObj.name,
                            values: (this.timelines.find(tl => tl.account == accObj.id)?.values ?? []).map((balance, balIdx) => {
                                let result = {
                                    y: balance / 100,
                                    x: this.$moment(this.startDate).add(balIdx, "days").toDate()
                                };
                                result.y = result.y * accRateProvider(balIdx) / this.reportRatesProvider(balIdx);
                                return result;
                            }),
                            color: chroma(color).hex(),
                        }
                    }
                )
            }
        }

    }
</script>

<style lang="scss" scoped>
    .apex-chart {
        min-width: 500px;
        min-height: 200px;
        padding: 0 0;
        margin: 0 0;
        width: 100%;
    }

    .legend-color-symbol {
        color: black;
        width: 1em;
        height: 1em;
        text-align: center;
        cursor: pointer;
        user-select: none;
    }

    .legend-caption {
        font-weight: bold;
    }

    .legend-item {
        & > * {
            margin-right: 0.5em;
        }

        display: grid;
        grid-template-columns: 1fr 6fr 5fr;
        width: 20em;
        align-items: center;
        padding: 0.25em;
        flex-flow: row;
    }

    .legend-container {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(25em, 1fr));
    }
</style>